import React, { useState, useEffect, useRef, useCallback } from 'react';
import { IonPage, IonContent } from '@ionic/react';
import { useAuthStore } from '../../store/authStore';
import { useChatStore, ChatMessage } from '../../store/chatStore';
import { usePollsStore } from '../../store/pollsStore';
import { wsClient } from '../../services/websocket';
import { api } from '../../services/api';
import { format, formatDistanceToNowStrict } from 'date-fns';
import BackHeader from '../../components/BackHeader';
import FeaturePageHeader from '../../components/FeaturePageHeader';
import FeatureMobileHeader from '../../components/FeatureMobileHeader';
import { useIsDesktop } from '../../hooks/useIsDesktop';
import Icon from '../../components/Icon';
import { showToast } from '../../components/Toast';
import ConfirmDialog from '../../components/ConfirmDialog';

const getInitials = (name: string) =>
  name.split(' ').map((n) => n[0]).join('').toUpperCase().slice(0, 2);

const formatDateLabel = (d: Date) => {
  const now = new Date();
  if (d.toDateString() === now.toDateString()) return 'Today';
  const yesterday = new Date(now);
  yesterday.setDate(yesterday.getDate() - 1);
  if (d.toDateString() === yesterday.toDateString()) return 'Yesterday';
  return format(d, 'EEEE d MMM');
};

const ChatPage: React.FC = () => {
  const isDesktopV2 = useIsDesktop();
  const { user, family } = useAuthStore();
  const {
    messages, isLoading, hasMore,
    fetchMessages, sendMessage: sendMsg, editMessage, deleteMessage,
    addReaction, removeReaction, togglePin, handleWsMessage,
  } = useChatStore();
  const { polls, fetchPolls, createPoll, castVote } = usePollsStore();

  const [draft, setDraft] = useState('');
  const [showAttachMenu, setShowAttachMenu] = useState(false);
  const [isSending, setIsSending] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editDraft, setEditDraft] = useState('');
  const [typingMembers, setTypingMembers] = useState<Record<string, { name: string; at: number }>>({});
  const typingStopTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [myTypingActive, setMyTypingActive] = useState(false);
  const [confirmDeleteId, setConfirmDeleteId] = useState<string | null>(null);
  const [newPollTitle, setNewPollTitle] = useState('');
  const [newPollOptions, setNewPollOptions] = useState(['', '']);
  const [showPollForm, setShowPollForm] = useState(false);
  const [chatSearch, setChatSearch] = useState('');
  const [showSearch, setShowSearch] = useState(false);
  const [reactTargetId, setReactTargetId] = useState<string | null>(null);

  const REACTION_EMOJIS = ['❤️', '😂', '👍', '🎉', '😮', '😢'];

  const toggleReaction = async (messageId: string, emoji: string) => {
    const msg = messages.find(m => m.id === messageId);
    if (!msg) return;
    const mine = msg.reactions?.find(r => r.emoji === emoji && r.user_id === user?.id);
    try {
      if (mine) await removeReaction(messageId, emoji);
      else await addReaction(messageId, emoji);
    } catch {
      /* swallow — optimistic updates are not critical */
    }
    setReactTargetId(null);
  };

  const scrollBoxRef = useRef<HTMLDivElement>(null);
  const bottomAnchorRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const imageInputRef = useRef<HTMLInputElement>(null);
  const isNearBottomRef = useRef(true);
  const prevCountRef = useRef(0);

  // ── Keyboard-aware height (prevents keyboard from hiding input) ──
  // With Capacitor Keyboard.resize: 'native' the WebView itself shrinks when
  // the keyboard opens, so visualViewport.height === window.innerHeight and
  // both reflect the visible area. On desktop we reclaim the 72px reserved
  // for the bottom nav.
  const [shellHeight, setShellHeight] = useState<string | undefined>(undefined);
  useEffect(() => {
    const vv = window.visualViewport;
    const update = () => {
      const navHeight = window.innerWidth >= 1024 ? 0 : 72;
      const h = vv?.height ?? window.innerHeight;
      setShellHeight(`${h - navHeight}px`);
    };
    update();
    vv?.addEventListener('resize', update);
    window.addEventListener('resize', update);
    return () => {
      vv?.removeEventListener('resize', update);
      window.removeEventListener('resize', update);
    };
  }, []);

  // ── Data ──
  useEffect(() => { fetchMessages(); fetchPolls(); }, [fetchMessages, fetchPolls]);
  useEffect(() => {
    const unsubs = [
      wsClient.on('CHAT_MESSAGE', handleWsMessage),
      wsClient.on('CHAT_MESSAGE_EDITED', handleWsMessage),
      wsClient.on('CHAT_MESSAGE_DELETED', handleWsMessage),
      wsClient.on('CHAT_MESSAGE_PINNED', handleWsMessage),
      // chat-mobile.pdf "Adesoji is typing…" — backend broadcasts
      // CHAT_TYPING with { user_id, display_name, state }. We keep a
      // Record<user_id, {name, at}> and auto-expire entries after 5s.
      wsClient.on('CHAT_TYPING', (msg: any) => {
        const p = msg?.payload;
        if (!p?.user_id || p.user_id === user?.id) return;
        if (p.state === 'stop') {
          setTypingMembers(prev => {
            const next = { ...prev };
            delete next[p.user_id];
            return next;
          });
        } else {
          setTypingMembers(prev => ({ ...prev, [p.user_id]: { name: p.display_name, at: Date.now() } }));
        }
      }),
    ];
    return () => unsubs.forEach(u => u());
  }, [handleWsMessage, user?.id]);

  // Expire stale typing entries after 5s so the indicator always fades.
  useEffect(() => {
    const t = setInterval(() => {
      setTypingMembers(prev => {
        const now = Date.now();
        const next: typeof prev = {};
        Object.entries(prev).forEach(([k, v]) => { if (now - v.at < 5000) next[k] = v; });
        return next;
      });
    }, 1500);
    return () => clearInterval(t);
  }, []);

  // Send typing start on keystroke; send stop after 2s of no keystrokes.
  const notifyTyping = useCallback(() => {
    if (!myTypingActive) {
      setMyTypingActive(true);
      api.post('/chat/typing', { state: 'start' }).catch(() => {});
    }
    if (typingStopTimerRef.current) clearTimeout(typingStopTimerRef.current);
    typingStopTimerRef.current = setTimeout(() => {
      setMyTypingActive(false);
      api.post('/chat/typing', { state: 'stop' }).catch(() => {});
    }, 2000);
  }, [myTypingActive]);

  // ── Scroll tracking ──
  const onScroll = useCallback(() => {
    const el = scrollBoxRef.current;
    if (!el) return;
    isNearBottomRef.current = el.scrollHeight - el.scrollTop - el.clientHeight < 150;
  }, []);

  const scrollToBottom = useCallback((instant = false) => {
    const el = scrollBoxRef.current;
    if (!el) return;
    requestAnimationFrame(() => {
      el.scrollTo({ top: el.scrollHeight, behavior: instant ? 'auto' : 'smooth' });
    });
  }, []);

  // Scroll on message changes
  useEffect(() => {
    const wasEmpty = prevCountRef.current === 0;
    const isNew = messages.length > prevCountRef.current;
    prevCountRef.current = messages.length;

    if (wasEmpty && messages.length > 0) {
      // Initial load — jump to bottom instantly
      scrollToBottom(true);
    } else if (isNew && isNearBottomRef.current) {
      // New message while near bottom — smooth scroll
      scrollToBottom(false);
    }
  }, [messages, scrollToBottom]);

  // ── Actions ──
  const sendMessage = async () => {
    if (!draft.trim() || !user || isSending) return;
    setIsSending(true);
    try {
      await sendMsg({ content: draft.trim() });
      setDraft('');
      isNearBottomRef.current = true;
      scrollToBottom(false);
    } catch { showToast('Failed to send message', 'error'); }
    finally { setIsSending(false); }
  };

  const handleImagePick = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    e.target.value = '';
    if (file.size > 5 * 1024 * 1024) { showToast('Image must be under 5MB', 'error'); return; }
    try {
      const reader = new FileReader();
      reader.onload = () => {
        const img = new Image();
        img.onload = async () => {
          const MAX = 1200;
          let w = img.width, h = img.height;
          if (w > MAX || h > MAX) {
            if (w > h) { h = Math.round(h * MAX / w); w = MAX; } else { w = Math.round(w * MAX / h); h = MAX; }
          }
          const canvas = document.createElement('canvas');
          canvas.width = w; canvas.height = h;
          canvas.getContext('2d')!.drawImage(img, 0, 0, w, h);
          await sendMsg({ content: '', mediaUrl: canvas.toDataURL('image/jpeg', 0.7), mediaType: 'image/jpeg' });
          scrollToBottom(false);
        };
        img.src = reader.result as string;
      };
      reader.readAsDataURL(file);
    } catch { showToast('Failed to send image', 'error'); }
    setShowAttachMenu(false);
  };

  const handleEdit = (msg: ChatMessage) => { setEditingId(msg.id); setEditDraft(msg.content); };
  const submitEdit = () => { if (editingId && editDraft.trim()) { editMessage(editingId, editDraft.trim()); setEditingId(null); setEditDraft(''); } };
  const cancelEdit = () => { setEditingId(null); setEditDraft(''); };
  const handleDelete = (id: string) => setConfirmDeleteId(id);
  const confirmDelete = () => { if (confirmDeleteId) { deleteMessage(confirmDeleteId); showToast('Message deleted', 'success'); setConfirmDeleteId(null); } };

  // ── Message grouping (5-min window) ──
  const getGroupInfo = (index: number) => {
    const msg = messages[index];
    const prev = messages[index - 1];
    const next = messages[index + 1];
    const samePrev = prev?.sender_id === msg.sender_id && prev && Math.abs(new Date(msg.created_at).getTime() - new Date(prev.created_at).getTime()) < 300000;
    const sameNext = next?.sender_id === msg.sender_id && next && Math.abs(new Date(next.created_at).getTime() - new Date(msg.created_at).getTime()) < 300000;
    return { isFirstInGroup: !samePrev, isLastInGroup: !sameNext, showTimestamp: !sameNext, showAvatar: !sameNext };
  };

  // ─── Desktop v2 (editorial header + chat stream layout) ────────────────
  if (isDesktopV2) {
    const membersList = family?.members || [];
    const memberAvatars = membersList.slice(0, 5).map(m => (
      <div key={m.id} style={{ background: m.colour || '#c5512d' }}>{getInitials(m.displayName)}</div>
    ));
    return (
      <IonPage>
        <IonContent scrollY={false}>
          <div className="fhv2-chat-page">
            <div className="fhv2-chat-wrap">
                <div className="fhv2-chat-stream">
                  <div className="fhv2-chat-head">
                    <div style={{ minWidth: 0 }}>
                      <div style={{ color: '#fff', fontWeight: 600, fontSize: 15 }}>{family?.name || 'Your family'}</div>
                      <div className="fhv2-chat-head-sub">
                        {(() => {
                          const typers = Object.values(typingMembers).map(t => t.name.split(' ')[0]);
                          if (typers.length > 0) {
                            const who = typers.length === 1 ? typers[0] : typers.length === 2 ? `${typers[0]} & ${typers[1]}` : `${typers.length} people`;
                            return <span style={{ color: 'var(--fhv2-forest-light)' }}>{who} {typers.length === 1 ? 'is typing…' : 'are typing…'}</span>;
                          }
                          const activeSet = new Set(
                            messages
                              .filter(m => (Date.now() - new Date(m.created_at).getTime()) < 10 * 60000)
                              .map(m => m.sender_id)
                          );
                          const onlineCount = membersList.filter(m => activeSet.has(m.id)).length;
                          const base = `${membersList.length} ${membersList.length === 1 ? 'member' : 'people'}`;
                          const names = membersList.length > 0 ? ` · ${membersList.map(m => m.displayName.split(' ')[0]).join(', ')}` : '';
                          const online = onlineCount > 0 ? ` — ${onlineCount} online` : '';
                          return `${base}${names}${online}`;
                        })()}
                      </div>
                    </div>
                    <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
                      <div className="fhv2-fam-avatars">{memberAvatars}</div>
                      <span className="fhv2-chat-head-count">{membersList.length}</span>
                      <button
                        className="fhv2-cic"
                        style={{ width: 34, height: 34, borderRadius: 10, background: 'rgba(255,255,255,0.05)', border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--fhv2-muted)' }}
                        aria-label="More"
                      >
                        <Icon name="more_horiz" size={16} color="var(--fhv2-muted)" />
                      </button>
                    </div>
                  </div>

                  {/* Pinned banner — shows most recent pinned message */}
                  {(() => {
                    const pinned = [...messages].filter(m => m.pinned_at).sort((a, b) => new Date(b.pinned_at!).getTime() - new Date(a.pinned_at!).getTime())[0];
                    if (!pinned) return null;
                    return (
                      <div className="fhv2-chat-pinned">
                        <Icon name="push_pin" size={14} color="var(--fhv2-terra)" />
                        <div className="fhv2-chat-pinned-body">
                          <b>Pinned by {pinned.sender_display_name?.split(' ')[0] || 'someone'}</b>
                          <span> · {pinned.content}</span>
                        </div>
                        <span className="fhv2-chat-pinned-time">{formatDistanceToNowStrict(new Date(pinned.pinned_at!))} ago</span>
                      </div>
                    );
                  })()}

                  <div ref={scrollBoxRef} onScroll={onScroll} className="fhv2-messages">
                    {hasMore && (
                      <button
                        onClick={() => fetchMessages(messages[0]?.id)}
                        disabled={isLoading}
                        style={{ alignSelf: 'center', background: 'rgba(255,255,255,0.04)', border: '1px solid var(--fhv2-line)', borderRadius: 999, padding: '6px 16px', color: 'var(--fhv2-muted)', fontSize: 12, cursor: 'pointer' }}
                      >
                        {isLoading ? 'Loading…' : 'Load more'}
                      </button>
                    )}
                    {messages.length === 0 && !isLoading && (
                      <div style={{ textAlign: 'center', padding: 40, color: 'var(--fhv2-muted)' }}>
                        <div style={{ fontFamily: 'var(--fhv2-serif)', fontStyle: 'italic', fontSize: 24, color: '#fff', marginBottom: 6 }}>Start the conversation.</div>
                        <div style={{ fontSize: 13 }}>Say hello to your family.</div>
                      </div>
                    )}
                    {messages.map((m, i) => {
                      const self = m.sender_id === user?.id;
                      const prev = messages[i - 1];
                      const prevDate = prev ? new Date(prev.created_at).toDateString() : '';
                      const thisDate = new Date(m.created_at).toDateString();
                      const showDivider = prevDate !== thisDate;
                      const senderColor = membersList.find(mm => mm.id === m.sender_id)?.colour || '#6f8a86';
                      const groupedReacts = (m.reactions || []).reduce<Record<string, { count: number; mine: boolean }>>((acc, r) => {
                        if (!acc[r.emoji]) acc[r.emoji] = { count: 0, mine: false };
                        acc[r.emoji].count += 1;
                        if (r.user_id === user?.id) acc[r.emoji].mine = true;
                        return acc;
                      }, {});
                      const reactEntries = Object.entries(groupedReacts);
                      return (
                        <React.Fragment key={m.id}>
                          {showDivider && <div className="fhv2-day-divider">{formatDateLabel(new Date(m.created_at))}</div>}
                          <div className={`fhv2-msg ${self ? 'is-self' : ''}`}>
                            <div className="fhv2-av" style={{ background: senderColor }}>{getInitials(m.sender_display_name || '??')}</div>
                            <div style={{ minWidth: 0 }}>
                              {!self && (
                                <div className="fhv2-msg-header">
                                  <span>{m.sender_display_name}</span>
                                  <span>·</span>
                                  <span>{format(new Date(m.created_at), 'HH:mm')}</span>
                                </div>
                              )}
                              <div className="fhv2-bubble" onClick={() => setReactTargetId(reactTargetId === m.id ? null : m.id)} style={{ cursor: 'pointer', padding: m.poll_id ? 0 : undefined, background: m.poll_id ? 'transparent' : undefined }}>
                                {m.poll_id ? (() => {
                                  // chat-mobile.pdf inline poll card.
                                  const poll = polls.find(p => p.id === m.poll_id);
                                  if (!poll) return m.content || <em style={{ color: 'var(--fhv2-muted)' }}>Poll unavailable</em>;
                                  const totalVotes = poll.total_votes || 0;
                                  const winningId = poll.options.reduce((a, b) => a.vote_count > b.vote_count ? a : b).id;
                                  return (
                                    <div style={{ background: 'rgba(255,255,255,0.06)', border: '1px solid var(--fhv2-line)', borderRadius: 12, padding: 14, minWidth: 260 }}>
                                      <div style={{ fontFamily: 'var(--fhv2-mono)', fontSize: 10, letterSpacing: '0.1em', color: 'var(--fhv2-terra)', marginBottom: 6, textTransform: 'uppercase' }}>
                                        POLL · {poll.total_votes}/{(family?.members?.length) || poll.total_votes}
                                      </div>
                                      <div style={{ fontFamily: 'var(--fhv2-serif)', fontStyle: 'italic', fontSize: 18, color: '#fff', marginBottom: 10 }}>{poll.title}</div>
                                      <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                                        {poll.options.slice(0, 4).map(opt => {
                                          const pct = totalVotes > 0 ? Math.round((opt.vote_count / totalVotes) * 100) : 0;
                                          const isWinning = opt.id === winningId && totalVotes > 0;
                                          return (
                                            <button
                                              key={opt.id}
                                              onClick={(e) => { e.stopPropagation(); castVote(poll.id, opt.id); }}
                                              className="fhv2-unset"
                                              style={{
                                                position: 'relative', padding: '8px 12px', textAlign: 'left',
                                                background: isWinning ? 'rgba(197,81,45,0.14)' : 'rgba(255,255,255,0.04)',
                                                border: `1px solid ${isWinning ? 'rgba(197,81,45,0.45)' : 'var(--fhv2-line)'}`,
                                                borderRadius: 8, cursor: 'pointer', overflow: 'hidden',
                                              }}
                                            >
                                              <div style={{ position: 'absolute', left: 0, top: 0, bottom: 0, width: `${pct}%`, background: isWinning ? 'rgba(197,81,45,0.18)' : 'rgba(255,255,255,0.04)' }} />
                                              <div style={{ position: 'relative', display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: 13 }}>
                                                <span style={{ color: '#fff' }}>{opt.user_voted ? '✓ ' : ''}{opt.label}</span>
                                                <span style={{ fontFamily: 'var(--fhv2-mono)', fontSize: 11, color: 'var(--fhv2-muted)' }}>{pct}%</span>
                                              </div>
                                            </button>
                                          );
                                        })}
                                      </div>
                                    </div>
                                  );
                                })() : (
                                  m.content || (m.media_url ? <img src={m.media_url} alt="" style={{ maxWidth: 280, borderRadius: 8, display: 'block' }} loading="lazy" /> : null)
                                )}
                              </div>
                              <div className="fhv2-msg-meta">
                                {self && <><span>You</span>
                                <span>·</span>
                                <span>{format(new Date(m.created_at), 'HH:mm')}</span></>}
                                {m.pinned_at && <><span>·</span><Icon name="push_pin" size={11} color="var(--fhv2-warn)" /></>}
                                <button
                                  onClick={(e) => { e.stopPropagation(); togglePin(m.id).catch(() => {}); }}
                                  aria-label={m.pinned_at ? 'Unpin' : 'Pin'}
                                  style={{ background: 'transparent', border: 'none', cursor: 'pointer', padding: 2, display: 'flex', alignItems: 'center' }}
                                >
                                  <Icon name={m.pinned_at ? 'push_pin' : 'push_pin'} size={12} color={m.pinned_at ? 'var(--fhv2-warn)' : 'var(--fhv2-muted)'} />
                                </button>
                                {self && (
                                  <>
                                    <button onClick={(e) => { e.stopPropagation(); handleEdit(m); }} aria-label="Edit" style={{ background: 'transparent', border: 'none', cursor: 'pointer', padding: 2, display: 'flex' }}>
                                      <Icon name="edit" size={12} color="var(--fhv2-muted)" />
                                    </button>
                                    <button onClick={(e) => { e.stopPropagation(); handleDelete(m.id); }} aria-label="Delete" style={{ background: 'transparent', border: 'none', cursor: 'pointer', padding: 2, display: 'flex' }}>
                                      <Icon name="delete" size={12} color="var(--fhv2-muted)" />
                                    </button>
                                  </>
                                )}
                              </div>
                              {reactEntries.length > 0 && (
                                <div className="fhv2-msg-reactions">
                                  {reactEntries.map(([emoji, info]) => (
                                    <button
                                      key={emoji}
                                      onClick={() => toggleReaction(m.id, emoji)}
                                      className={info.mine ? 'mine' : ''}
                                    >
                                      {emoji} {info.count}
                                    </button>
                                  ))}
                                </div>
                              )}
                              {reactTargetId === m.id && (
                                <div style={{ marginTop: 6, display: 'flex', gap: 4, background: 'rgba(255,255,255,0.05)', padding: 4, borderRadius: 999, alignSelf: 'flex-start', border: '1px solid var(--fhv2-line)' }}>
                                  {REACTION_EMOJIS.map(e => (
                                    <button
                                      key={e}
                                      onClick={() => toggleReaction(m.id, e)}
                                      style={{ width: 28, height: 28, borderRadius: '50%', background: 'transparent', border: 'none', fontSize: 16, cursor: 'pointer' }}
                                    >
                                      {e}
                                    </button>
                                  ))}
                                </div>
                              )}
                            </div>
                          </div>
                        </React.Fragment>
                      );
                    })}
                    <div ref={bottomAnchorRef} />
                  </div>

                  <form
                    className="fh-composer"
                    onSubmit={e => { e.preventDefault(); sendMessage(); }}
                  >
                    <button
                      type="button"
                      className="fh-composer__action"
                      onClick={() => imageInputRef.current?.click()}
                      aria-label="Attach photo"
                    >
                      <Icon name="image" size={20} color="currentColor" />
                    </button>
                    <div className="fh-composer__pill">
                      <input
                        ref={inputRef}
                        className="fh-composer__input"
                        value={draft}
                        onChange={e => { setDraft(e.target.value); notifyTyping(); }}
                        placeholder="Message the family…"
                      />
                    </div>
                    <button
                      type="submit"
                      className="fh-composer__action fh-composer__send"
                      disabled={!draft.trim() || isSending}
                      aria-label="Send message"
                    >
                      <Icon name="send" size={20} color="currentColor" />
                    </button>
                  </form>
                </div>

                <div className="fhv2-stack">
                  <div className="fhv2-card">
                    <div className="fhv2-card-head"><h4>Members</h4><span className="fhv2-more">{membersList.length}</span></div>
                    {membersList.map(m => {
                      // Last message time from this sender — basis for activity line.
                      const lastMsg = [...messages].reverse().find(msg => msg.sender_id === m.id);
                      let activity = '';
                      if (lastMsg) {
                        const mins = (Date.now() - new Date(lastMsg.created_at).getTime()) / 60000;
                        if (mins < 5) activity = 'Active now';
                        else if (mins < 60) activity = `Last seen ${Math.round(mins)} min`;
                        else if (mins < 1440) activity = `Last seen ${Math.round(mins / 60)}h ago`;
                        else activity = `Last seen ${Math.round(mins / 1440)}d ago`;
                      } else {
                        activity = 'Quiet';
                      }
                      const roleTag = m.id === user?.id
                        ? (m.role === 'admin' ? 'you · admin' : 'you')
                        : (m.role === 'admin' ? 'admin' : '');
                      return (
                        <div className="fhv2-row" key={m.id}>
                          <div className="fhv2-avatar" style={{ background: m.colour || '#c5512d', width: 28, height: 28 }}>{getInitials(m.displayName)}</div>
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <div className="title" style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                              <span>{m.displayName}</span>
                              {roleTag && <span className="fhv2-member-tag">{roleTag}</span>}
                            </div>
                            <div className="sub">{activity}</div>
                          </div>
                          {activity === 'Active now' && <span className="fhv2-member-dot" />}
                        </div>
                      );
                    })}
                  </div>

                  {/* Pinned list */}
                  {(() => {
                    const pinnedList = messages.filter(m => m.pinned_at).sort((a, b) => new Date(b.pinned_at!).getTime() - new Date(a.pinned_at!).getTime()).slice(0, 5);
                    if (pinnedList.length === 0) return null;
                    return (
                      <div className="fhv2-card">
                        <div className="fhv2-card-head"><h4>Pinned</h4><span className="fhv2-more">{pinnedList.length}</span></div>
                        {pinnedList.map(p => {
                          const pColor = membersList.find(mm => mm.id === p.sender_id)?.colour || 'var(--fhv2-terra)';
                          return (
                            <div className="fhv2-row" key={p.id} style={{ cursor: 'pointer' }} onClick={() => togglePin(p.id).catch(() => {})}>
                              <div style={{ width: 4, height: 28, borderRadius: 2, background: pColor, flexShrink: 0 }} />
                              <div style={{ flex: 1, minWidth: 0 }}>
                                <div className="title" style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{p.content || 'Image'}</div>
                                <div className="sub">{p.sender_display_name} · {format(new Date(p.pinned_at!), 'd MMM')}</div>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    );
                  })()}

                  {/* Shared media — 3x3 grid with overflow pill */}
                  {(() => {
                    const allMedia = messages.filter(m => m.media_url);
                    if (allMedia.length === 0) return null;
                    const mediaMsgs = allMedia.slice(-9).reverse();
                    const extra = Math.max(allMedia.length - 9, 0);
                    return (
                      <div className="fhv2-card">
                        <div className="fhv2-card-head"><h4>Shared media</h4><span className="fhv2-more">{allMedia.length}</span></div>
                        <div className="fhv2-chat-media-grid">
                          {mediaMsgs.map((m, i) => (
                            <div key={m.id} className="fhv2-chat-media-cell" style={{ background: `url(${m.media_url}) center/cover` }}>
                              {i === mediaMsgs.length - 1 && extra > 0 && (
                                <span className="fhv2-chat-media-more">+{extra}</span>
                              )}
                            </div>
                          ))}
                        </div>
                      </div>
                    );
                  })()}
                </div>
              </div>
            </div>
          <ConfirmDialog
            open={confirmDeleteId !== null}
            title="Delete message?"
            message="This can't be undone."
            confirmLabel="Delete"
            onConfirm={() => { if (confirmDeleteId) { deleteMessage(confirmDeleteId); setConfirmDeleteId(null); } }}
            onCancel={() => setConfirmDeleteId(null)}
          />
        </IonContent>
      </IonPage>
    );
  }

  // Most-recent pinned message (mobile mirrors the desktop banner)
  const mobilePinned = [...messages]
    .filter(m => m.pinned_at)
    .sort((a, b) => new Date(b.pinned_at!).getTime() - new Date(a.pinned_at!).getTime())[0];

  return (
    <IonPage>
      <IonContent scrollY={false}>
        <div className="flex flex-col fh-chat-shell" style={{ background: 'var(--fh-dark-bg)', fontFamily: 'Manrope, sans-serif', ...(shellHeight ? { height: shellHeight } : {}) }}>
          {/* Header */}
          <div style={{ flexShrink: 0 }}>
            <div style={{ padding: 'calc(12px + env(safe-area-inset-top, 0px)) 16px 4px' }}>
              <FeatureMobileHeader title={family?.name || 'Family chat'} rightIcon={showSearch ? 'close' : 'search'} onRight={() => { setShowSearch(s => !s); if (showSearch) setChatSearch(''); }} />
              <div style={{ textAlign: 'center', fontSize: 11, color: 'var(--fhv2-muted)', marginTop: -8, marginBottom: 4 }}>
                {(() => {
                  const typers = Object.values(typingMembers).map(t => t.name.split(' ')[0]);
                  if (typers.length > 0) {
                    const who = typers.length === 1 ? typers[0] : typers.length === 2 ? `${typers[0]} & ${typers[1]}` : `${typers.length} people`;
                    const verb = typers.length === 1 ? 'is typing…' : 'are typing…';
                    return <span style={{ color: 'var(--fhv2-forest-light)' }}>{who} {verb}</span>;
                  }
                  const mediaCount = messages.filter(m => m.media_url).length;
                  const base = family?.members?.length
                    ? `${family.members.length} people`
                    : '0 members';
                  return mediaCount > 0
                    ? `${base} · ${mediaCount} shared ${mediaCount === 1 ? 'photo' : 'photos'}`
                    : base;
                })()}
              </div>
              {mobilePinned && !showSearch && (
                <div
                  onClick={() => { /* tap to reveal pinned list in future */ }}
                  style={{
                    display: 'flex', alignItems: 'center', gap: 8,
                    background: 'rgba(197,81,45,0.1)', border: '1px solid rgba(197,81,45,0.3)',
                    borderRadius: 12, padding: '10px 12px', margin: '6px 0',
                    fontSize: 12, color: 'rgba(255,255,255,0.88)', cursor: 'pointer',
                  }}
                >
                  <Icon name="push_pin" size={12} color="var(--fhv2-terra)" />
                  <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    <b>Pinned by {mobilePinned.sender_display_name?.split(' ')[0] || 'someone'}</b>
                    <span style={{ color: 'var(--fhv2-muted)' }}> · {mobilePinned.content}</span>
                  </span>
                </div>
              )}
              {showSearch && (
                <div style={{ padding: '8px 0 4px', display: 'flex', gap: 8, alignItems: 'center' }}>
                  <input
                    autoFocus
                    value={chatSearch}
                    onChange={e => setChatSearch(e.target.value)}
                    placeholder="Search messages…"
                    style={{ flex: 1, padding: '10px 12px', borderRadius: 10, background: 'rgba(255,255,255,0.05)', border: '1px solid var(--fhv2-line)', color: '#fff', fontSize: 13, outline: 'none' }}
                  />
                  {chatSearch && (
                    <span style={{ fontSize: 11, color: 'var(--fhv2-muted)', fontFamily: 'var(--fhv2-mono)', letterSpacing: '0.5px' }}>
                      {messages.filter(m => (m.content || '').toLowerCase().includes(chatSearch.toLowerCase())).length} MATCHES
                    </span>
                  )}
                </div>
              )}
            </div>
          </div>

          {/* Messages area */}
          <div
            ref={scrollBoxRef}
            onScroll={onScroll}
            className="flex-1 overflow-y-auto px-4 pt-4 space-y-2"
            style={{ minHeight: 0, maxWidth: '42rem', marginLeft: 'auto', marginRight: 'auto', width: '100%', overscrollBehavior: 'contain', paddingBottom: 'calc(72px + env(safe-area-inset-bottom, 0px) + 12px)' }}
          >
            {hasMore && (
              <div className="flex justify-center py-2">
                <button onClick={() => fetchMessages(messages[0]?.id)} disabled={isLoading}
                  className="text-xs font-semibold px-4 py-1.5 rounded-full"
                  style={{ background: 'var(--fh-dark-card)', color: 'var(--fh-dark-text-secondary)', border: '1px solid var(--fh-dark-border)' }}>
                  {isLoading ? 'Loading...' : 'Load more'}
                </button>
              </div>
            )}

            {messages.length === 0 && !isLoading && (
              <div className="flex flex-col items-center justify-center pt-20 text-center px-6">
                <Icon name="chat_bubble" size={52} color="var(--fh-dark-text-muted)" />
                <p className="font-semibold mb-1" style={{ color: 'var(--fh-dark-text-secondary)' }}>Start the conversation</p>
                <p className="text-sm mb-5" style={{ color: 'var(--fh-dark-text-muted)' }}>Say hello to your family!</p>
                <button onClick={() => inputRef.current?.focus()}
                  className="px-6 py-2.5 rounded-xl text-sm font-bold text-white active:scale-95 transition-transform"
                  style={{ background: 'linear-gradient(135deg, var(--fh-primary), var(--fh-primary-light))' }}>
                  Send a message
                </button>
              </div>
            )}

            {messages.filter(m => !chatSearch.trim() || (m.content || '').toLowerCase().includes(chatSearch.toLowerCase())).map((msg, index, filtered) => {
              const isMe = msg.sender_id === user?.id;
              const colour = msg.sender_colour;
              const isEditing = editingId === msg.id;
              const group = getGroupInfo(index);
              const msgDate = new Date(msg.created_at);
              const prevMsg = filtered[index - 1];
              const showDateSep = !prevMsg || msgDate.toDateString() !== new Date(prevMsg.created_at).toDateString();
              const groupedReacts = (msg.reactions || []).reduce<Record<string, { count: number; mine: boolean }>>((acc, r) => {
                if (!acc[r.emoji]) acc[r.emoji] = { count: 0, mine: false };
                acc[r.emoji].count += 1;
                if (r.user_id === user?.id) acc[r.emoji].mine = true;
                return acc;
              }, {});
              const reactEntries = Object.entries(groupedReacts);

              return (
                <div key={msg.id}>
                  {showDateSep && (
                    <div className="flex items-center justify-center py-3">
                      <span className="text-xs font-bold px-3 py-1 rounded-full" style={{ background: 'var(--fh-overlay-light)', color: 'var(--fh-dark-text-muted)' }}>
                        {formatDateLabel(msgDate)}
                      </span>
                    </div>
                  )}
                  <div className={`relative group ${group.isFirstInGroup ? '' : '-mt-1.5'}`}>
                    <div className={`flex gap-2 items-end ${isMe ? 'flex-row-reverse' : 'flex-row'}`}>
                      {!isMe && (
                        group.showAvatar ? (
                          <div className="w-7 h-7 rounded-full flex items-center justify-center text-white text-xs font-bold flex-shrink-0" style={{ background: colour }}>
                            {getInitials(msg.sender_display_name)}
                          </div>
                        ) : <div className="w-7 flex-shrink-0" />
                      )}
                      <div className={`max-w-[75%] ${isMe ? 'items-end' : 'items-start'} flex flex-col gap-0.5`}>
                        {!isMe && group.isFirstInGroup && (
                          <span className="text-xs font-bold px-1" style={{ color: colour }}>{msg.sender_display_name.split(' ')[0]}</span>
                        )}
                        {isEditing ? (
                          <div className="flex items-center gap-2 w-full">
                            <input value={editDraft} onChange={e => setEditDraft(e.target.value)}
                              onKeyDown={e => { if (e.key === 'Enter') submitEdit(); if (e.key === 'Escape') cancelEdit(); }}
                              autoFocus className="flex-1 px-3 py-2 rounded-xl text-sm outline-none"
                              style={{ background: 'var(--fh-dark-card)', color: 'var(--fh-dark-text)', border: '1px solid rgba(197,81,45,0.5)' }} />
                            <button onClick={submitEdit} style={{ background: 'none', border: 'none', padding: 4, cursor: 'pointer' }}><Icon name="check" size={18} color="#81c784" /></button>
                            <button onClick={cancelEdit} style={{ background: 'none', border: 'none', padding: 4, cursor: 'pointer' }}><Icon name="close" size={18} color="#ff8a65" /></button>
                          </div>
                        ) : (
                          <div className="relative">
                            <div
                              onClick={() => setReactTargetId(reactTargetId === msg.id ? null : msg.id)}
                              style={{
                                background: isMe ? 'var(--fhv2-terra)' : 'rgba(255, 255, 255, 0.05)',
                                color: '#fff',
                                padding: '10px 14px',
                                borderRadius: isMe ? '14px 14px 4px 14px' : '14px 14px 14px 4px',
                                fontSize: 13.5,
                                lineHeight: 1.45,
                                wordWrap: 'break-word',
                                cursor: 'pointer',
                              }}
                            >
                              {msg.media_url && <img src={msg.media_url} alt="" style={{ maxWidth: '100%', borderRadius: 8, marginBottom: msg.content ? 6 : 0, maxHeight: 240, objectFit: 'cover', display: 'block' }} loading="lazy"/>}
                              {msg.content}
                            </div>
                            <div className="absolute top-1/2 -translate-y-1/2 opacity-0 group-hover:opacity-100 transition-opacity flex gap-0.5" style={{ [isMe ? 'right' : 'left' as const]: '100%', [isMe ? 'marginRight' : 'marginLeft' as const]: 4 } as any}>
                              <button onClick={(e) => { e.stopPropagation(); togglePin(msg.id).catch(() => {}); }} aria-label={msg.pinned_at ? 'Unpin' : 'Pin'} style={{ background: 'var(--fh-overlay-medium)', border: 'none', borderRadius: 6, padding: 4, cursor: 'pointer' }}><Icon name="push_pin" size={14} color={msg.pinned_at ? 'var(--fhv2-warn)' : 'var(--fh-dark-text-secondary)'} /></button>
                              {isMe && (
                                <>
                                  <button onClick={(e) => { e.stopPropagation(); handleEdit(msg); }} aria-label="Edit" style={{ background: 'var(--fh-overlay-medium)', border: 'none', borderRadius: 6, padding: 4, cursor: 'pointer' }}><Icon name="edit" size={14} color="var(--fh-dark-text-secondary)" /></button>
                                  <button onClick={(e) => { e.stopPropagation(); handleDelete(msg.id); }} aria-label="Delete" style={{ background: 'var(--fh-overlay-medium)', border: 'none', borderRadius: 6, padding: 4, cursor: 'pointer' }}><Icon name="delete" size={14} color="#ef5350" /></button>
                                </>
                              )}
                            </div>
                            {msg.pinned_at && (
                              <div aria-label="Pinned" style={{ position: 'absolute', top: -6, [isMe ? 'right' : 'left' as const]: -6, width: 18, height: 18, borderRadius: '50%', background: 'rgba(233,196,106,0.9)', display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: '0 2px 6px rgba(0,0,0,0.3)' } as any}>
                                <Icon name="push_pin" size={10} color="#0f1923" />
                              </div>
                            )}
                            {reactTargetId === msg.id && (
                              <div style={{ position: 'absolute', bottom: 'calc(100% + 4px)', [isMe ? 'right' : 'left' as const]: 0, background: '#0f1923', border: '1px solid var(--fhv2-line)', borderRadius: 999, padding: '4px 6px', display: 'flex', gap: 2, boxShadow: '0 4px 16px rgba(0,0,0,0.5)', zIndex: 20 } as any}>
                                {REACTION_EMOJIS.map(e => (
                                  <button
                                    key={e}
                                    onClick={(ev) => { ev.stopPropagation(); toggleReaction(msg.id, e); }}
                                    className="fhv2-unset"
                                    style={{ width: 32, height: 32, borderRadius: '50%', background: 'transparent', fontSize: 18, cursor: 'pointer' }}
                                  >
                                    {e}
                                  </button>
                                ))}
                              </div>
                            )}
                          </div>
                        )}
                        {reactEntries.length > 0 && (
                          <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap', marginTop: 3, alignSelf: isMe ? 'flex-end' : 'flex-start' }}>
                            {reactEntries.map(([emoji, info]) => (
                              <button
                                key={emoji}
                                onClick={() => toggleReaction(msg.id, emoji)}
                                className="fhv2-unset"
                                style={{
                                  fontSize: 11, padding: '2px 8px', borderRadius: 999,
                                  background: info.mine ? 'rgba(197,81,45,0.18)' : 'rgba(255,255,255,0.05)',
                                  border: '1px solid ' + (info.mine ? 'rgba(197,81,45,0.4)' : 'var(--fhv2-line)'),
                                  color: '#fff', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 3,
                                }}
                              >
                                {emoji} {info.count}
                              </button>
                            ))}
                          </div>
                        )}
                        {group.showTimestamp && (
                          <div className="flex items-center gap-1 px-1">
                            <span className="text-xs" style={{ color: 'var(--fh-dark-text-muted)' }}>{format(new Date(msg.created_at), 'HH:mm')}</span>
                            {msg.edited_at && <span className="text-xs" style={{ color: 'var(--fh-dark-text-muted)' }}>edited</span>}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
            <div ref={bottomAnchorRef} />
          </div>

          {/* Input bar */}
          <div className="flex-shrink-0" style={{ background: 'var(--fh-dark-surface-alt)', borderTop: '1px solid var(--fh-dark-border)' }}>
            <input ref={imageInputRef} type="file" accept="image/*" onChange={handleImagePick} style={{ display: 'none' }} />

            {showAttachMenu && !showPollForm && (
              <div style={{ display: 'flex', gap: 16, padding: '10px 16px', borderBottom: '1px solid var(--fh-dark-border)', justifyContent: 'center' }}>
                <button onClick={() => { imageInputRef.current?.click(); setShowAttachMenu(false); }}
                  style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4, background: 'none', border: 'none', cursor: 'pointer' }}>
                  <div style={{ width: 44, height: 44, borderRadius: 12, background: 'rgba(100,181,246,0.12)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}><Icon name="image" size={22} color="#64b5f6" /></div>
                  <span style={{ fontSize: 11, fontWeight: 600, color: 'var(--fh-dark-text-secondary)' }}>Photo</span>
                </button>
                <button onClick={() => { setShowPollForm(true); setShowAttachMenu(false); }}
                  style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4, background: 'none', border: 'none', cursor: 'pointer' }}>
                  <div style={{ width: 44, height: 44, borderRadius: 12, background: 'rgba(156,39,176,0.12)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}><Icon name="how_to_vote" size={22} color="#9c27b0" /></div>
                  <span style={{ fontSize: 11, fontWeight: 600, color: 'var(--fh-dark-text-secondary)' }}>Poll</span>
                </button>
                <button onClick={() => { imageInputRef.current?.click(); setShowAttachMenu(false); }}
                  style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4, background: 'none', border: 'none', cursor: 'pointer' }}>
                  <div style={{ width: 44, height: 44, borderRadius: 12, background: 'rgba(129,199,132,0.12)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}><Icon name="photo_camera" size={22} color="#81c784" /></div>
                  <span style={{ fontSize: 11, fontWeight: 600, color: 'var(--fh-dark-text-secondary)' }}>Camera</span>
                </button>
              </div>
            )}

            {showPollForm && (
              <div style={{ padding: '12px 16px 8px', borderBottom: '1px solid var(--fh-dark-border)' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                  <span style={{ fontSize: 13, fontWeight: 700, color: '#9c27b0' }}>Create Poll</span>
                  <button onClick={() => { setShowPollForm(false); setNewPollTitle(''); setNewPollOptions(['', '']); }}
                    style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 4 }}><Icon name="close" size={16} color="var(--fh-dark-text-muted)" /></button>
                </div>
                <input value={newPollTitle} onChange={e => setNewPollTitle(e.target.value)} placeholder="What do you want to ask?"
                  style={{ width: '100%', background: 'var(--fh-overlay-light)', border: '1px solid var(--fh-dark-border)', borderRadius: 10, padding: '8px 12px', color: 'var(--fh-dark-text)', fontSize: 13, marginBottom: 6, outline: 'none', fontFamily: 'Manrope, sans-serif', boxSizing: 'border-box' }} />
                {newPollOptions.map((opt, i) => (
                  <input key={i} value={opt} onChange={e => { const o = [...newPollOptions]; o[i] = e.target.value; setNewPollOptions(o); }}
                    placeholder={`Option ${i + 1}`}
                    style={{ width: '100%', background: 'var(--fh-overlay-subtle)', border: '1px solid var(--fh-dark-border)', borderRadius: 8, padding: '6px 10px', color: 'var(--fh-dark-text)', fontSize: 12, marginBottom: 4, outline: 'none', fontFamily: 'Manrope, sans-serif', boxSizing: 'border-box' }} />
                ))}
                <div style={{ display: 'flex', gap: 8, marginTop: 6 }}>
                  <button onClick={() => setNewPollOptions([...newPollOptions, ''])} style={{ background: 'none', border: 'none', color: '#9c27b0', fontSize: 11, fontWeight: 700, cursor: 'pointer' }}>+ Option</button>
                  <div style={{ flex: 1 }} />
                  <button onClick={async () => {
                    const opts = newPollOptions.filter(o => o.trim());
                    if (!newPollTitle.trim() || opts.length < 2) { showToast('Need a question and at least 2 options', 'error'); return; }
                    try {
                      // Create the poll first, then post a chat message that
                      // references it so the inline poll card appears in-stream.
                      const created: any = await createPoll({ title: newPollTitle.trim(), options: opts });
                      const pollId = created?.id || (usePollsStore.getState().polls[0]?.id);
                      if (pollId) {
                        await sendMsg({ content: newPollTitle.trim(), pollId });
                      }
                      setShowPollForm(false); setNewPollTitle(''); setNewPollOptions(['', '']);
                      showToast('Poll posted', 'success');
                    } catch { showToast('Failed to create poll', 'error'); }
                  }} style={{ background: '#9c27b0', border: 'none', borderRadius: 8, padding: '5px 14px', color: '#fff', fontSize: 12, fontWeight: 700, cursor: 'pointer' }}>Post</button>
                </div>
              </div>
            )}

            <div className="fh-composer" style={{ maxWidth: '42rem', margin: '0 auto', width: '100%' }}>
              <button
                type="button"
                onClick={() => { setShowAttachMenu(!showAttachMenu); setShowPollForm(false); }}
                aria-label="Attach"
                className="fh-composer__action"
                style={{ transform: showAttachMenu ? 'rotate(45deg)' : 'none' }}
              >
                <Icon name="add" size={22} color={showAttachMenu ? '#80cbc4' : 'currentColor'} />
              </button>
              <div className="fh-composer__pill">
                <input
                  ref={inputRef}
                  type="text"
                  value={draft}
                  onChange={e => { setDraft(e.target.value); notifyTyping(); }}
                  onKeyDown={e => e.key === 'Enter' && sendMessage()}
                  placeholder="Message"
                  aria-label="Message input"
                  className="fh-composer__input"
                />
              </div>
              <button
                type="button"
                onClick={sendMessage}
                disabled={!draft.trim() || isSending}
                aria-label="Send message"
                className="fh-composer__action fh-composer__send"
              >
                <Icon name="send" size={20} color="currentColor" />
              </button>
            </div>
          </div>
        </div>

        <ConfirmDialog open={!!confirmDeleteId} title="Delete message" message="Are you sure you want to delete this message?"
          confirmLabel="Delete" onConfirm={confirmDelete} onCancel={() => setConfirmDeleteId(null)} />
      </IonContent>
    </IonPage>
  );
};

export default ChatPage;
